import { SERIES_SEGMENT_CLASSES, formatCount } from '@/lib/analyticsReport';
import { delegationBars } from '@/lib/dutyAnalytics';
import { cn } from '@/lib/utils';
import { DUTY_ANALYTICS_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C §2] HOW MUCH OF WHAT WAS ASKED OF A DIVISION IT HANDED DOWN — A BAR PER DIVISION, A SENTENCE
 *  BESIDE EACH, AND THE FIGURES IN A TABLE.
 *
 *  ⚠️ THE BAR IS `aria-hidden` AND THE SENTENCE IS THE CONTENT — `CoverageBar`'s R2. A screen reader
 *  hears "Of the 212 duties asked of Central Division, 120 were handed down to its units."; the bar
 *  is decoration for everybody else.
 *
 *  ⚠️ FLEX-GROW, SO NO PROPORTION EXISTS ANYWHERE (§2d). Each segment's `flex-grow` is an integer count
 *  over a zero basis — handed down, kept, and the room up to the longest bar — and the browser draws
 *  the lengths. Neutral ink (`SERIES_SEGMENT_CLASSES`, measured by `theme/analyticsTint.test.ts`),
 *  never a status hue: handing work down is neither good nor bad.
 *
 *  ⚠️ DIVISION GRAIN ONLY. Handed-down positions sit on the units' shares, so a unit row could show
 *  more handed down than asked; the commissionerate layout draws one bar per division and a division's
 *  own page one bar, from its total.
 *
 *  ⚠️ THE NUMBERS HERE DO NOT LINK, AND THAT IS THE ONE EXEMPTION FROM "EVERY NON-ZERO COUNT OPENS ITS
 *  ROWS" — the same counts open from the table above, and a sentence full of links reads as a menu.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = DUTY_ANALYTICS_COPY;

export const DelegationShape = ({
  rows,
  nameHeader,
}: {
  rows: readonly { orgUnitId: string; name: string; asked: number; handedDown: number }[];
  nameHeader: string;
}) => {
  const bars = delegationBars(rows);
  if (bars.length === 0) return <p className="text-sm text-muted-foreground">{copy.delegationNothingAsked}</p>;

  return (
    <div className="flex flex-col gap-4">
      <ul className="flex flex-col gap-3">
        {bars.map((bar) => (
          <li key={bar.orgUnitId} data-row={bar.orgUnitId} className="flex flex-col gap-1">
            <p className="text-sm">{copy.delegationSentence(bar.asked, bar.handedDown, bar.name)}</p>
            <div
              aria-hidden="true"
              data-testid="duty-analytics-delegation-bar"
              data-asked={bar.asked}
              data-handed-down={bar.drawnHandedDown}
              data-kept={bar.kept}
              data-max={bar.max}
              className="flex h-3 w-full overflow-hidden rounded-sm bg-muted"
            >
              <span
                data-segment="handed-down"
                className={cn('basis-0', SERIES_SEGMENT_CLASSES[0])}
                style={{ flexGrow: bar.drawnHandedDown }}
              />
              <span
                data-segment="kept"
                className={cn('basis-0', SERIES_SEGMENT_CLASSES[1])}
                style={{ flexGrow: bar.kept }}
              />
              <span data-segment="room" className="basis-0 bg-card" style={{ flexGrow: bar.room }} />
            </div>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-hidden="true">
        <span className="inline-flex items-center gap-1.5">
          <span className={cn('size-2.5 rounded-sm', SERIES_SEGMENT_CLASSES[0])} />
          {copy.delegationHandedDown}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className={cn('size-2.5 rounded-sm', SERIES_SEGMENT_CLASSES[1])} />
          {copy.delegationKept}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">{copy.delegationExemption}</p>
      <details>
        <summary className="cursor-pointer text-sm text-muted-foreground">{copy.delegationDetails}</summary>
        <table className="mt-2 text-sm" data-testid="duty-analytics-delegation-figures">
          <thead>
            <tr>
              <th scope="col" className="px-3 py-1 text-left font-medium">
                {nameHeader}
              </th>
              <th scope="col" className="px-3 py-1 text-right font-medium">
                {copy.columns.ASKED}
              </th>
              <th scope="col" className="px-3 py-1 text-right font-medium">
                {copy.delegationHandedDownHeader}
              </th>
              <th scope="col" className="px-3 py-1 text-right font-medium">
                {copy.delegationKeptHeader}
              </th>
            </tr>
          </thead>
          <tbody>
            {bars.map((bar) => (
              <tr key={bar.orgUnitId}>
                <th scope="row" className="px-3 py-1 text-left font-normal">
                  {bar.name}
                </th>
                <td className="px-3 py-1 text-right tabular-nums">{formatCount(bar.asked)}</td>
                <td className="px-3 py-1 text-right tabular-nums">{formatCount(bar.handedDown)}</td>
                <td className="px-3 py-1 text-right tabular-nums">{formatCount(bar.kept)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
};
